import { useMutation, useQuery } from '@apollo/client';
import {
  ChoiceGroup,
  DirectionalHint,
  IChoiceGroupOption,
  Icon,
  IconButton,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { cutToast } from 'utility/toast';
import React, { useEffect, useState } from 'react';
import {
  ApprovalTestGroupDelete,
  ApprovalTestGroupDeleteVariables,
} from '../__generated__/ApprovalTestGroupDelete';
import {
  ApprovalTestGroups,
  ApprovalTestGroups_approvalTestGroups_nodes_approvalTestItems_nodes,
} from '../__generated__/ApprovalTestGroups';
import { AddGroupModal } from '../AddGroupModal';
import APPROVAL_TEST_GROUP_DELETE from '../ApprovalTestGroupDelete.graphql';
import TEST_GROUPS from '../ApprovalTestGroups.graphql';
import { useStyles } from './index.styles';

type TestGroupsSelectorProps = {
  onChange: (
    key: string | undefined,
    groupItems: ApprovalTestGroups_approvalTestGroups_nodes_approvalTestItems_nodes[]
  ) => void;
  attachTestGroup?: boolean;
};

export const TestGroupsSelector: React.FC<TestGroupsSelectorProps> = ({
  onChange,
  attachTestGroup = false,
}) => {
  const { data: testGroupsList } = useQuery<ApprovalTestGroups>(TEST_GROUPS);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(
    testGroupsList?.approvalTestGroups?.nodes?.[0]?.id
  );

  const theme = useTheme();
  const styles = useStyles();
  const [deleteTestGroup] = useMutation<
    ApprovalTestGroupDelete,
    ApprovalTestGroupDeleteVariables
  >(APPROVAL_TEST_GROUP_DELETE);

  const options: IChoiceGroupOption[] =
    testGroupsList?.approvalTestGroups?.nodes?.map((group, currentIndex) => ({
      key: group.id,
      text: '',

      onRenderLabel: () => {
        const [hovered, setHovered] = useState(false);

        return (
          <Stack
            horizontal
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            verticalAlign="start"
            className={styles.labelStyle}
          >
            <TooltipHost
              content={group.name}
              directionalHint={DirectionalHint.topCenter}
            >
              <Stack
                horizontal
                verticalAlign="start"
                className={styles.labelSubStyle}
              >
                <Stack style={{ marginTop: 5 }}>
                  <Icon iconName="FabricFolder" />
                </Stack>

                <Text variant="large">
                  {group.name.length > 21
                    ? `${group.name.slice(0, 18)}...`
                    : group.name}
                </Text>
                <Text
                  variant="large"
                  style={{ color: theme.palette.themePrimary }}
                >
                  ({group?.approvalTestItems?.nodes?.length || 0})
                </Text>
              </Stack>
            </TooltipHost>

            <Stack
              horizontal
              verticalAlign="start"
              tokens={{ childrenGap: 4 }}
              className={styles.iconsRow}
              style={{
                marginTop: -5,
                width: 30,
                visibility: hovered ? 'visible' : 'hidden',
              }}
            >
              <AddGroupModal
                isEdit
                selectedGroup={
                  testGroupsList?.approvalTestGroups?.nodes?.[currentIndex]
                }
              />
              <IconButton
                onClick={async () => {
                  const variables = {
                    input: {
                      entityDelete: [
                        { id: group.id!, rowTimestamp: group._rowTimestamp! },
                      ],
                    },
                  };
                  const { errors } = await deleteTestGroup({ variables });

                  if (errors?.length) cutToast.error(errors[0].message);
                  else {
                    cutToast.success('Test group deleted successfully');
                  }
                }}
                iconProps={{ iconName: 'Delete' }}
                ariaLabel="Delete"
              />
            </Stack>
          </Stack>
        );
      },
    })) || [];

  useEffect(() => {
    const selected = testGroupsList?.approvalTestGroups?.nodes?.find(
      (g) => g.id === selectedKey
    );
    onChange(selectedKey, selected?.approvalTestItems?.nodes ?? []);
  }, [testGroupsList]);

  return (
    <Stack
      style={{
        width: 340,
        borderRight: attachTestGroup
          ? `1px solid ${theme.palette.neutralLight}`
          : undefined,
      }}
    >
      <Stack style={{ paddingTop: 10 }}>
        <ChoiceGroup
          defaultSelectedKey={selectedKey}
          options={options}
          onChange={(e, option) => {
            const selected = testGroupsList?.approvalTestGroups?.nodes?.find(
              (g) => g.id === option?.key
            );
            setSelectedKey(option?.key);
            onChange(option?.key, selected?.approvalTestItems?.nodes ?? []);
          }}
        />
      </Stack>
    </Stack>
  );
};
